import React, { useMemo, useState } from 'react';
import { parseSovereignHtml } from '../utils/sovereignReportParser';
import { buildSovereignCaseFileHtml } from '../utils/sovereignReportHtml';

function FieldCard({ row }) {
  if (row.heading === 'Profile') {
    return (
      <div className="border border-teal-500/20 rounded-lg bg-gray-950 p-4 md:p-5">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">DISC Profile</div>
        <div className="text-3xl md:text-4xl font-bold text-teal-300 mb-3">{row.content}</div>
        {row.source && <div className="text-sm text-gray-500">Source: {row.source}</div>}
      </div>
    );
  }
  return (
    <div className="border border-teal-500/20 rounded-lg bg-gray-950 p-4 md:p-5">
      <div className="font-semibold text-teal-200 text-base md:text-lg mb-2 leading-snug">
        {row.heading}
      </div>
      <div className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">{row.content}</div>
      {row.source && <div className="text-sm text-gray-500 mt-3">Source: {row.source}</div>}
    </div>
  );
}

function TargetCardList({ targets }) {
  if (!targets?.length) return null;
  return (
    <ol className="space-y-4 list-none pl-0">
      {targets.map((target) => (
        <li
          key={`${target.num}-${target.name}`}
          className="border border-teal-500/20 rounded-lg bg-gray-950 p-4 md:p-5"
        >
          <div className="flex items-start gap-3 mb-3">
            <span className="text-teal-400 font-bold text-sm mt-0.5 shrink-0">{target.num}.</span>
            <h3 className="text-teal-200 font-semibold text-base md:text-lg leading-snug">
              {target.name}
            </h3>
          </div>
          <dl className="space-y-2 text-sm">
            {[
              ['Angle', target.angle],
              ['Evidence', target.evidence],
              ['Risk', target.risk],
              ['Role', target.role],
              ['Relationship', target.relationship],
            ]
              .filter(([, value]) => Boolean(value))
              .map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
                  <dd className="text-gray-300 leading-relaxed whitespace-pre-wrap">{value}</dd>
                </div>
              ))}
          </dl>
        </li>
      ))}
    </ol>
  );
}

export default function SovereignReportViewer({
  htmlContent,
  reportTitle,
  className = '',
  onDownloadHtml,
  savedTier1Draft,
  onTier1DraftChange,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showOriginal, setShowOriginal] = useState(false);
  const parsed = useMemo(() => (htmlContent ? parseSovereignHtml(htmlContent) : null), [htmlContent]);
  const notes = savedTier1Draft?.investigator_notes || '';
  const downloadCaseFile = () => {
    if (!parsed?.sections?.length) return;
    const html = buildSovereignCaseFileHtml(parsed, reportTitle, {
      investigatorNotes: notes,
    });
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${String(reportTitle || 'Sovereign_Assessment').replace(/[^\w-]+/g, '_')}_CaseFile.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (!htmlContent) {
    return (
      <div className={`flex items-center justify-center h-full min-h-[28rem] text-gray-500 text-sm ${className}`}>
        <p>No report generated yet.</p>
      </div>
    );
  }

  if (!parsed || parsed.sections.length === 0) {
    return (
      <div className={`h-full min-h-[28rem] ${className}`}>
        <iframe
          title={reportTitle || 'Sovereign Report'}
          className="w-full h-full bg-white"
          srcDoc={htmlContent}
          sandbox="allow-same-origin"
        />
      </div>
    );
  }

  const section = parsed.sections[activeIndex] || parsed.sections[0];
  const isTargetTab = Boolean(section.targets?.length);
  const isDiscTab = /disc|communication profile/i.test(section.title);

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`}>
      <div className="shrink-0 border-b border-teal-500/30 bg-gray-950 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-gray-400 truncate">{reportTitle || 'Sovereign Report'}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowOriginal((value) => !value)}
            className="px-2 sm:px-3 py-1.5 text-xs border border-gray-600 text-gray-400 rounded-md hover:border-gray-500"
          >
            {showOriginal ? 'Structured view' : 'Original HTML'}
          </button>
          <button
            type="button"
            onClick={downloadCaseFile}
            className="px-2 sm:px-3 py-1.5 text-xs border border-teal-500/30 text-teal-300 rounded-md hover:bg-teal-500/10"
            title="Download structured case file"
          >
            Download Case File
          </button>
          {onDownloadHtml && (
            <button
              type="button"
              onClick={onDownloadHtml}
              className="px-2 sm:px-3 py-1.5 text-xs border border-gray-600 text-gray-400 rounded-md hover:border-gray-500"
              title="Download original HTML"
            >
              Original HTML
            </button>
          )}
        </div>
      </div>
      {showOriginal ? (
        <iframe
          title={reportTitle || 'Sovereign Report'}
          className="flex-1 min-h-[28rem] w-full bg-white"
          srcDoc={htmlContent}
          sandbox="allow-same-origin"
        />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <div className="lg:w-64 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-teal-500/30 bg-gray-950">
            <div className="p-2 lg:p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 lg:mb-2 px-1">
                Sections
              </p>
              <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible gap-1.5 lg:gap-1 pb-2 lg:pb-0 -mx-2 px-2 lg:mx-0 lg:px-0">
                {parsed.sections.map((item, idx) => (
                  <button
                    key={`${item.title}-${idx}`}
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    className={`shrink-0 lg:w-full text-left px-2.5 py-1.5 lg:px-3 lg:py-2 rounded text-xs lg:text-sm transition-colors whitespace-nowrap lg:whitespace-normal ${
                      activeIndex === idx
                        ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40 font-medium'
                        : 'bg-gray-900 text-gray-400 border border-transparent hover:text-teal-300 hover:bg-gray-800'
                    }`}
                  >
                    <div className="leading-tight">{item.title}</div>
                    {item.rowCount > 0 && (
                      <div className="hidden lg:block text-xs text-gray-500 mt-0.5">
                        {item.rowCount} item{item.rowCount === 1 ? '' : 's'}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0 overflow-y-auto bg-gray-900">
            <div className="p-3 md:p-6 lg:max-w-4xl">
              <h2 className="text-xl md:text-2xl font-semibold text-teal-300 mb-4 md:mb-6">{section.title}</h2>
              {isTargetTab && <TargetCardList targets={section.targets} />}
              {section.rows?.length > 0 && !isTargetTab ? (
                <div className="space-y-4 md:space-y-3">
                  {section.rows.map((row, idx) => (
                    <FieldCard key={`${row.heading}-${idx}`} row={row} />
                  ))}
                  {isDiscTab &&
                    !section.rows.some((row) => /how to use|how this shapes|how to communicate/i.test(row.heading)) && (
                      <div className="border border-amber-500/30 rounded-lg bg-amber-950/20 p-4 text-sm text-amber-200">
                        DISC usage belongs on this tab. Regenerate if How to use this profile is missing.
                      </div>
                    )}
                </div>
              ) : null}
              {!isTargetTab && !section.rows?.length && (
                <div className="text-base text-gray-500 italic py-8 text-center">No structured data available</div>
              )}
              {onTier1DraftChange && (
                <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-teal-500/20">
                  <h3 className="text-lg md:text-xl font-semibold text-teal-300 mb-4 md:mb-6">
                    Investigator Notes
                  </h3>
                  <textarea
                    value={notes}
                    onChange={(event) =>
                      onTier1DraftChange({
                        ...(savedTier1Draft || {}),
                        investigator_notes: event.target.value,
                      })
                    }
                    placeholder="Case observations, follow-ups, red flags..."
                    className="w-full px-4 py-3 bg-gray-950 border border-teal-500/20 rounded-lg text-gray-300 text-base leading-relaxed placeholder-gray-600 focus:outline-none min-h-[6rem] md:min-h-[8rem] resize-y"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
